/** Shared centered hero for marketing sub-pages. */

'use client';

import React from 'react';
import { cn } from '@/lib/utils/utils';
import { MotionSection } from '@/components/marketing/motion-section';
import { MktgHeading } from '@/components/marketing/mktg-heading';
import { MktgBodyText } from '@/components/marketing/mktg-body-text';
import { YellowHighlight } from '@/components/marketing/yellow-highlight';
import { YellowDot } from '@/components/marketing/yellow-dot';
import { TrustBadges } from '@/components/marketing/trust-badges';
import { AnimatedCounter } from '@/components/marketing/animated-counter';

interface MarketingPageHeroProps {
  title: string;
  description?: string;
  /** Small uppercase eyebrow label above the title */
  badge?: string;
  /** Words in the title to wrap with YellowHighlight (draw variant) */
  highlightWords?: string[];
  /** Show TrustBadges row beneath the description */
  trustBadges?: boolean;
  /** Show an AnimatedCounter stat beneath the description */
  stat?: { value: number; suffix?: string; label: string };
  children?: React.ReactNode;
  className?: string;
}

/** Renders the title with optional YellowHighlight on specific words */
function HighlightedTitle({
  title,
  highlightWords
}: {
  title: string;
  highlightWords?: string[];
}) {
  if (!highlightWords || highlightWords.length === 0) {
    return <>{title}</>;
  }

  // Build regex to split on highlight words (case-insensitive)
  const pattern = new RegExp(
    `(${highlightWords.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
    'gi'
  );
  const parts = title.split(pattern);

  return (
    <>
      {parts.map((part, i) => {
        const isHighlight = highlightWords.some(
          (w) => w.toLowerCase() === part.toLowerCase()
        );
        return isHighlight ? (
          <YellowHighlight key={i} variant='draw'>
            {part}
          </YellowHighlight>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        );
      })}
    </>
  );
}

export function MarketingPageHero({
  title,
  description,
  badge,
  highlightWords,
  trustBadges,
  stat,
  children,
  className
}: MarketingPageHeroProps) {
  return (
    <section
      aria-label={title}
      className={cn('py-12 md:py-20', className)}
      style={{
        backgroundImage: [
          'linear-gradient(135deg, var(--mktg-hero-tint) 0%, var(--mktg-neutral) 40%, var(--mktg-white) 100%)',
          'radial-gradient(circle at 20% 80%, rgba(77, 92, 255, 0.03) 0%, transparent 50%)',
          'radial-gradient(circle at 80% 20%, rgba(255, 209, 0, 0.03) 0%, transparent 50%)'
        ].join(', ')
      }}
    >
      <div
        className='mx-auto px-6 md:px-[72px]'
        style={{ maxWidth: '860px', textAlign: 'center' }}
      >
        <MotionSection>
          {badge && (
            <div className='mb-4 flex items-center justify-center gap-2'>
              <YellowDot />
              <span
                className='mktg-body mktg-body-sm font-semibold tracking-widest uppercase'
                style={{ color: 'var(--mktg-gray)' }}
              >
                {badge}
              </span>
            </div>
          )}

          <MktgHeading as='h1' size='xl' className='mb-6'>
            <HighlightedTitle title={title} highlightWords={highlightWords} />
          </MktgHeading>

          {description && (
            <MktgBodyText
              size='lg'
              color='gray'
              className='mx-auto'
              style={{ maxWidth: '680px' }}
            >
              {description}
            </MktgBodyText>
          )}

          {stat && (
            <div
              className='mx-auto mt-6 flex items-center justify-center gap-2'
              style={{ color: 'var(--mktg-dark)' }}
            >
              <span
                className='mktg-heading'
                style={{ fontSize: '28px', color: 'var(--mktg-yellow)' }}
              >
                <AnimatedCounter
                  value={stat.value}
                  suffix={stat.suffix ?? ''}
                />
              </span>
              <MktgBodyText as='span' size='sm' color='gray'>
                {stat.label}
              </MktgBodyText>
            </div>
          )}

          {trustBadges && (
            <div style={{ marginTop: '24px' }}>
              <TrustBadges />
            </div>
          )}

          {children && <div style={{ marginTop: '32px' }}>{children}</div>}
        </MotionSection>
      </div>
    </section>
  );
}
